import React, { useState, useEffect } from 'react';
import { LineChart, RefreshCw, ArrowUpRight, ArrowDownRight, Minus, Activity, Zap, Layers, BarChart2 } from 'lucide-react';
import { marketService } from '../../services/api';

const TIMEFRAMES = ['1m', '5m', '15m', '1h', '4h', '1D'];

export default function SignalScannerGrid() {
  const [activeTimeframe, setActiveTimeframe] = useState('15m');
  const [signals, setSignals] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);

  const fetchSignals = async (tf = activeTimeframe) => {
    setIsLoading(true);
    try {
      const res = await marketService.getSignals(tf, 'BTC,ETH,SOL,BNB');
      if (res?.signals) {
        setSignals(res.signals);
        setLastUpdated(new Date().toLocaleTimeString());
      }
    } catch (err) {
      console.error('Error fetching market signals:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchSignals(activeTimeframe);
    const interval = setInterval(() => {
      fetchSignals(activeTimeframe);
    }, 15000); // 15s live refresh
    return () => clearInterval(interval);
  }, [activeTimeframe]);

  const getSignalBadge = (signal, score) => {
    if (signal === 'STRONG_BUY') {
      return (
        <span className="px-2.5 py-1 rounded-lg text-xs font-mono font-bold bg-emerald-500/20 text-emerald-400 border border-emerald-400/50 flex items-center gap-1 shadow-[0_0_15px_rgba(0,255,136,0.3)]">
          <ArrowUpRight className="w-3.5 h-3.5" />
          <span>STRONG BUY ({score})</span>
        </span>
      );
    }
    if (signal === 'BUY') {
      return (
        <span className="px-2.5 py-1 rounded-lg text-xs font-mono font-bold bg-emerald-500/10 text-emerald-400 border border-emerald-500/30 flex items-center gap-1">
          <ArrowUpRight className="w-3.5 h-3.5" />
          <span>BUY ({score})</span>
        </span>
      );
    }
    if (signal === 'STRONG_SELL' || signal === 'SELL') {
      return (
        <span className="px-2.5 py-1 rounded-lg text-xs font-mono font-bold bg-rose-500/20 text-rose-400 border border-rose-400/50 flex items-center gap-1 shadow-[0_0_15px_rgba(255,77,69,0.3)]">
          <ArrowDownRight className="w-3.5 h-3.5" />
          <span>{signal} ({score})</span>
        </span>
      );
    }
    return (
      <span className="px-2.5 py-1 rounded-lg text-xs font-mono font-semibold bg-slate-800 text-slate-300 border border-white/10 flex items-center gap-1">
        <Minus className="w-3.5 h-3.5" />
        <span>NEUTRAL ({score})</span>
      </span>
    );
  };

  return (
    <div className="space-y-6 animate-fade-in">
      
      {/* 1. Header Toolbar */}
      <div className="glass-panel p-4 sm:p-5 rounded-2xl border border-fuchsia-500/30 flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 rounded-xl bg-fuchsia-500/10 border border-fuchsia-400/40 flex items-center justify-center text-fuchsia-400 shadow-[0_0_15px_rgba(217,70,239,0.25)]">
            <LineChart className="w-5 h-5" />
          </div>
          <div>
            <h3 className="font-display font-bold text-base text-white uppercase tracking-wider">
              Real-time Algorithmic Signal Scanner
            </h3>
            <p className="text-xs font-mono text-slate-400">
              Đồng thuận đa chỉ báo (RSI, MACD, EMA Ribbon, Bollinger Bands, RVOL)
            </p>
          </div>
        </div>

        {/* Timeframe Bar & Refresh */}
        <div className="flex items-center space-x-2">
          <div className="bg-slate-950/80 p-1 rounded-xl border border-white/10 flex items-center space-x-1">
            {TIMEFRAMES.map((tf) => (
              <button
                key={tf}
                type="button"
                onClick={() => setActiveTimeframe(tf)}
                className={`px-3 py-1 rounded-lg text-xs font-mono font-semibold transition-all cursor-pointer ${
                  activeTimeframe === tf 
                    ? 'bg-fuchsia-500/20 text-fuchsia-300 border border-fuchsia-400/50 shadow-[0_0_10px_rgba(217,70,239,0.3)]' 
                    : 'text-slate-400 hover:text-slate-200'
                }`}
              >
                {tf}
              </button>
            ))}
          </div>

          <button
            type="button"
            disabled={isLoading}
            onClick={() => fetchSignals(activeTimeframe)}
            className="p-2 rounded-xl bg-slate-900 border border-white/10 text-slate-300 hover:text-fuchsia-400 hover:border-fuchsia-400/50 transition-all cursor-pointer"
            title="Làm mới tín hiệu"
          >
            <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin text-fuchsia-400' : ''}`} />
          </button>
        </div>
      </div>

      {/* 2. Signals Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {signals.map((item) => {
          const indicators = item.indicators || {};
          const isBull = item.score > 30;
          const isBear = item.score < -30;

          return (
            <div 
              key={item.symbol}
              className="glass-card p-5 rounded-2xl border border-white/10 hover:border-fuchsia-500/40 transition-all flex flex-col justify-between space-y-4 group"
            >
              <div>
                {/* Symbol and Badge */}
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-display font-bold text-lg text-white tracking-wide">
                      {item.symbol}
                    </h4>
                    <span className="text-[10px] font-mono text-slate-400">SPOT // PERPETUAL</span>
                  </div>
                  {getSignalBadge(item.signal, item.score)}
                </div>

                {/* Confluence Score Bar */}
                <div className="mt-4 space-y-1">
                  <div className="flex justify-between text-[11px] font-mono">
                    <span className="text-slate-400">Confluence Score</span>
                    <span className={`font-bold ${isBull ? 'text-emerald-400' : isBear ? 'text-rose-400' : 'text-slate-300'}`}>
                      {item.score} / 100
                    </span>
                  </div>
                  <div className="w-full bg-slate-900 h-2 rounded-full overflow-hidden border border-white/10">
                    <div 
                      className={`h-full rounded-full transition-all duration-500 ${
                        isBull ? 'bg-gradient-to-r from-cyan-400 to-emerald-400' : isBear ? 'bg-gradient-to-r from-amber-400 to-rose-500' : 'bg-slate-600'
                      }`}
                      style={{ width: `${Math.max(5, Math.abs(item.score))}%` }}
                    />
                  </div>
                </div>

                {/* Indicator Details */}
                <div className="mt-4 pt-3 border-t border-white/10 grid grid-cols-2 gap-2 text-xs font-mono">
                  <div className="p-2 rounded-lg bg-slate-950/40 border border-white/5">
                    <span className="text-[10px] text-slate-500 block">RSI (14)</span>
                    <span className="font-bold text-slate-200">{indicators.rsi ?? '50.0'}</span>
                  </div>

                  <div className="p-2 rounded-lg bg-slate-950/40 border border-white/5">
                    <span className="text-[10px] text-slate-500 block">RVOL (20)</span>
                    <span className="font-bold text-fuchsia-400">{indicators.rvol ? `${indicators.rvol}x` : '1.0x'}</span>
                  </div>

                  <div className="p-2 rounded-lg bg-slate-950/40 border border-white/5 col-span-2">
                    <span className="text-[10px] text-slate-500 block">MACD STRUCTURE</span>
                    <span className="font-bold text-cyan-300 truncate block">{indicators.macd || 'NEUTRAL'}</span>
                  </div>

                  <div className="p-2 rounded-lg bg-slate-950/40 border border-white/5 col-span-2">
                    <span className="text-[10px] text-slate-500 block">EMA TREND</span>
                    <span className="font-bold text-emerald-300 truncate block">{indicators.emaTrend || 'FLAT'}</span>
                  </div>
                </div>
              </div>

              {/* Status Footer */}
              <div className="flex items-center justify-between text-[10px] font-mono text-slate-500 pt-2 border-t border-white/5">
                <span className="flex items-center gap-1">
                  <Zap className="w-3 h-3 text-fuchsia-400" />
                  <span>SMC ALGO CONFLUENCE</span>
                </span>
                <span>TF: {activeTimeframe}</span>
              </div>
            </div>
          );
        })}
      </div>

      {lastUpdated && (
        <div className="text-right text-[11px] font-mono text-slate-500">
          Cập nhật lần cuối lúc: <strong className="text-slate-400">{lastUpdated}</strong> &bull; Quét tự động mỗi 15s
        </div>
      )}

    </div>
  );
}
